'use client';

import {
  useState,
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import { UnifiedImageCropModal } from './UnifiedImageCropModal';
import {
  FileUploadDropZone,
  type FileUploadDropZoneRef,
} from './FileUploadDropZone';
import {
  MediaSourceSelector,
  type MediaSourceExtraOption,
  type MediaSourceProjectAngle,
} from './MediaSourceSelector';
import { useTranslations } from 'next-intl';
import { toast } from '@/shared/lib/toast';
import { getDeviceCapabilities, isMobileDevice } from '@/shared/lib/device';
import { validateFile, isImageFile } from '@/shared/lib/file-validation';
import { resolveImageSizePolicy } from '@/shared/lib/services/image-upload.service';
import type { ReactNode } from 'react';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('UnifiedImageUpload');

export interface UnifiedImageUploadProps {
  // Upload configuration
  uploadType: 'angle' | 'reference' | 'control';
  /**
   * Whether this is an add/create flow or a replace flow. Drives the
   * contextual media-source picker title (e.g. "Add angle" vs "Replace
   * angle"). Defaults to 'create'.
   */
  mode?: 'create' | 'replace';
  conceptId?: string;

  // Multiple file support
  allowMultiple?: boolean;
  maxFiles?: number;

  // Crop configuration
  allowCrop?: boolean;
  cropAspectRatio?: number;
  minCropWidth?: number;
  minCropHeight?: number;
  /** Free-aspect crop mode (frame snaps to nearest supported ratio on release) */
  freeAspectRatio?: boolean;

  // Render picker configuration
  /** Show option to select from existing renders */
  showRendersOption?: boolean;
  /** Additional options for MediaSourceSelector (e.g. "Select from library") */
  mediaSourceExtraOptions?: MediaSourceExtraOption[];
  /** Optional thumbnail strip of existing project angles in the selector. */
  mediaSourceProjectAngles?: MediaSourceProjectAngle[];
  /** Tap on a project-angle thumbnail → consumer attaches the angle. */
  onMediaSourceProjectAngleSelected?: (angleId: string) => void;
  /** User abandoned the picker without selecting anything (sheet dismissed or
   *  native file dialog cancelled). Transient consumers that mount this flow
   *  with autoOpen must reset their `open` flag here, or the flow can never
   *  be retriggered. */
  onMediaSourceDismiss?: () => void;

  // UI configuration
  children?: ReactNode;
  className?: string;
  accept?: string;
  uploadTitle?: string;
  uploadDescription?: string;

  // File type configuration
  allowedTypes?: string[]; // Custom allowed MIME types (defaults to images only)

  /** Transparent drop zone — no border/cursor, only drag-and-drop */
  transparent?: boolean;

  // Callbacks
  onUploadComplete?: (urls: string[]) => void;
  onUploadError?: (error: Error) => void;
  /**
   * Pre-flight notification with the validated selection. Returning `false`
   * (sync or async) vetoes the whole flow — no crop queue, no upload — so
   * consumers can gate on plan limits before anything visible happens.
   * `void`/`true` lets the flow proceed.
   */
  onFilesSelected?: (files: File[]) => boolean | void | Promise<boolean | void>;
  onFilesProcessed?: (files: File[]) => void; // Called with cropped files
  /**
   * Opt-in instant optimistic preview. When provided, confirming the crop
   * surfaces a synchronous preview and closes the cropper immediately while
   * compression + upload run in the background (the consumer shows an inline
   * placeholder card, e.g. the reference library). Surfaces with their own
   * post-processing progress UI (e.g. angle upload modal) omit it and keep the
   * legacy "cropper stays open until the blob is ready" behavior.
   */
  onInstantPreview?: (previewUrl: string | null) => void;
}

export interface UnifiedImageUploadRef {
  openFileDialog: () => void;
  /**
   * Inject a pre-selected file set straight into the same pipeline the file
   * dialog / drop zone use (validation → crop → upload). Lets an external
   * orchestrator (e.g. the Brief unified intake) reuse this flow without
   * re-opening a picker.
   */
  processFiles: (files: File[]) => void;
}

interface CropQueueItem {
  file: File;
  objectUrl: string;
  index: number;
}

export const UnifiedImageUpload = forwardRef<
  UnifiedImageUploadRef,
  UnifiedImageUploadProps
>(function UnifiedImageUpload(
  {
    uploadType,
    mode = 'create',
    allowMultiple = false,
    maxFiles,
    allowCrop = true,
    cropAspectRatio,
    minCropWidth = 100,
    minCropHeight = 100,
    freeAspectRatio = false,
    showRendersOption = false,
    mediaSourceExtraOptions,
    mediaSourceProjectAngles,
    onMediaSourceProjectAngleSelected,
    onMediaSourceDismiss,
    children,
    className = '',
    accept = 'image/*',
    uploadTitle,
    uploadDescription,
    allowedTypes,
    transparent = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onUploadComplete,
    onUploadError,
    onFilesSelected,
    onFilesProcessed,
    onInstantPreview,
  },
  ref
) {
  const fileUploadRef = useRef<FileUploadDropZoneRef>(null);
  const [cropQueue, setCropQueue] = useState<CropQueueItem[]>([]);
  const [currentCropIndex, setCurrentCropIndex] = useState(0);
  const [processedFiles, setProcessedFiles] = useState<File[]>([]);
  const [showMediaSourceSelector, setShowMediaSourceSelector] = useState(false);

  // Instant-path accumulation (decoupled from `processedFiles`, which the
  // legacy path uses): each confirmed crop compresses in the background, and
  // `onFilesProcessed` fires once every image in the queue has settled.
  // `instantGenRef` invalidates in-flight background compressions when the
  // batch is cancelled or replaced, so a stale blob can't resurrect a discarded
  // upload or leave an orphaned placeholder card.
  const instantAccumRef = useRef<File[]>([]);
  const instantPendingRef = useRef(0);
  const instantGenRef = useRef(0);
  const tMedia = useTranslations('mediaSource');
  const tAngles = useTranslations('angles');
  const tReference = useTranslations('projectReference');
  const tUpload = useTranslations('fileUpload');

  // Contextual picker title driven by uploadType + mode, so each surface
  // (add angle, replace angle, add reference, upload control sketch) gets the
  // right heading automatically. Reuses existing keys where they already
  // describe the action. Falls back to the generic count-aware titles only
  // for unexpected combinations.
  const pickerTitle = useMemo(() => {
    if (uploadType === 'angle') {
      return mode === 'replace'
        ? tMedia('titleReplaceAngle')
        : tAngles('addAngle');
    }
    if (uploadType === 'reference') {
      return tReference('panel.addReference');
    }
    if (uploadType === 'control') {
      return tMedia('titleUploadSketch');
    }

    const isFilesMode = accept?.includes('pdf') || accept?.includes('text/');
    const effectiveMax = maxFiles ?? 1;
    if (allowMultiple && effectiveMax > 1) {
      return isFilesMode
        ? tMedia('titleFilesUpTo', { count: effectiveMax })
        : tMedia('titleImagesUpTo', { count: effectiveMax });
    }
    return isFilesMode ? tMedia('titleFile') : tMedia('titleImage');
  }, [
    uploadType,
    mode,
    accept,
    allowMultiple,
    maxFiles,
    tMedia,
    tAngles,
    tReference,
  ]);

  // Contextual picker subtitle explaining what to upload. Reuses the existing
  // empty-state copy (already localized) for the angle/view flow — the only
  // context with a ready-made "what kind of image" sentence. Other contexts
  // rely on the contextual title alone.
  const pickerSubtitle = useMemo(() => {
    if (uploadType === 'angle' && mode !== 'replace') {
      return tAngles('noAnglesDescription');
    }
    return undefined;
  }, [uploadType, mode, tAngles]);

  // Log device capabilities on mount for debugging mobile issues
  useEffect(() => {
    const capabilities = getDeviceCapabilities();
    const isMobile = isMobileDevice();

    logger.info('Component mounted with device info:', {
      isMobile,
      capabilities,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
      },
      platform: navigator.platform,
    });

    // Show warning for low-end devices
    if (capabilities.isLowEndDevice) {
      console.warn(
        '[UnifiedImageUpload] Low-end device detected - may have limited performance'
      );
    }
  }, []);

  // Define helper functions first (before they're used in callbacks)
  const handleBatchUpload = useCallback(
    async (files: File[]) => {
      // For direct upload without crop, just notify parent
      onFilesProcessed?.(files);
    },
    [onFilesProcessed]
  );

  const cleanupCropQueue = useCallback(() => {
    // Cleanup object URLs
    cropQueue.forEach((item) => URL.revokeObjectURL(item.objectUrl));
    setCropQueue([]);
    setCurrentCropIndex(0);
    setProcessedFiles([]);
  }, [cropQueue]);

  const handleFilesReceived = useCallback(
    async (files: File[]) => {
      logger.info('Files received:', files.length);

      if (files.length === 0) {
        logger.debug('No files to process');
        return;
      }

      // Apply maxFiles limit — single truncation point for every entry path
      // (drop zone, file dialog, media-source selector). Truncation is
      // surfaced to the user instead of silently dropping files.
      const limitedFiles = maxFiles ? files.slice(0, maxFiles) : files;
      if (maxFiles && limitedFiles.length < files.length) {
        toast.warning(tUpload('tooManyFiles', { count: maxFiles }));
      }
      logger.debug('Files after limit:', limitedFiles.length);

      // Validate all files before processing (use custom allowedTypes if provided)
      const validationResults = limitedFiles.map((file) => ({
        file,
        validation: validateFile(file, allowedTypes),
      }));

      // Separate valid and invalid files
      const validFiles = validationResults
        .filter((r) => r.validation.valid)
        .map((r) => r.file);

      const invalidFiles = validationResults.filter((r) => !r.validation.valid);

      // Show errors for invalid files
      if (invalidFiles.length > 0) {
        console.error(
          '[UnifiedImageUpload] Invalid files detected:',
          invalidFiles
        );

        invalidFiles.forEach((item) => {
          const validationError = item.validation.error;
          toast.error(
            validationError
              ? tUpload(validationError.code, validationError.params)
              : tUpload('validationFailed')
          );
        });

        // If all files are invalid, stop processing
        if (validFiles.length === 0) {
          logger.debug('All files are invalid, stopping');
          return;
        }

        // Show warning that some files were skipped
        toast.warning(
          tUpload('filesSkippedInvalid', {
            skipped: invalidFiles.length,
            valid: validFiles.length,
          })
        );
      }

      logger.info('Valid files to process:', validFiles.length);

      // Pre-flight veto: the consumer can block the flow (e.g. plan-limit
      // paywall) before the crop queue or any upload starts.
      const proceed = await onFilesSelected?.(validFiles);
      if (proceed === false) {
        logger.info('File selection vetoed by consumer preflight');
        return;
      }

      // Separate image files (can be cropped) from non-image files (direct upload)
      const imageFiles = validFiles.filter(isImageFile);
      const nonImageFiles = validFiles.filter((f) => !isImageFile(f));

      logger.debug(
        'Image files:',
        imageFiles.length,
        'Non-image files:',
        nonImageFiles.length
      );

      // Non-image files (PDFs, text) go directly to upload without crop
      if (nonImageFiles.length > 0) {
        logger.info(
          'Sending non-image files directly to upload:',
          nonImageFiles.length
        );
        handleBatchUpload(nonImageFiles);
      }

      // Process image files
      if (imageFiles.length === 0) {
        // No images to process - we're done (non-images already sent to upload)
        logger.debug('No image files to crop');
        return;
      }

      if (allowCrop) {
        logger.info('Creating crop queue for', imageFiles.length, 'image(s)');

        // Create crop queue with object URLs, handling errors for each file individually
        const queueResults = imageFiles.map((file, index) => {
          try {
            logger.debug(`Creating object URL for file ${index + 1}:`, {
              name: file.name,
              size: file.size,
              type: file.type,
            });

            const objectUrl = URL.createObjectURL(file);

            if (!objectUrl) {
              throw new Error('Failed to create object URL (returned empty)');
            }

            return {
              success: true as const,
              item: {
                file,
                objectUrl,
                index,
              },
            };
          } catch (error) {
            console.error(
              `[UnifiedImageUpload] Error creating object URL for file "${file.name}":`,
              error
            );
            return {
              success: false as const,
              file,
              error: error as Error,
            };
          }
        });

        // Separate successful and failed items
        const successfulItems = queueResults
          .filter((r) => r.success)
          .map((r) => r.success && r.item);

        const failedItems = queueResults.filter((r) => !r.success);

        // Show errors for failed items
        if (failedItems.length > 0) {
          console.error(
            '[UnifiedImageUpload] Failed to create object URLs for some files:',
            failedItems
          );

          failedItems.forEach((item) => {
            if (!item.success) {
              toast.error(tUpload('imageLoadFailed', { name: item.file.name }));
            }
          });

          // If all files failed, stop processing
          if (successfulItems.length === 0) {
            console.error(
              '[UnifiedImageUpload] Failed to create object URLs for all files'
            );
            toast.error(tUpload('allImagesLoadFailed'));
            onUploadError?.(
              new Error('Failed to create object URLs for all files')
            );
            return;
          }

          // Show warning that some files were skipped
          toast.warning(
            tUpload('imagesLoadFailedPartial', {
              failed: failedItems.length,
              ok: successfulItems.length,
            })
          );
        }

        const queue = successfulItems as CropQueueItem[];
        logger.info('Crop queue created with', queue.length, 'items');

        // Reset instant-path accumulation for this new batch (new generation).
        instantAccumRef.current = [];
        instantPendingRef.current = queue.length;
        instantGenRef.current += 1;

        // Use functional setState to ensure atomic update
        setCropQueue(() => queue);
        setCurrentCropIndex(() => 0);
        setProcessedFiles(() => []);

        logger.debug('Crop queue state updated, modal should appear');
      } else {
        logger.info('Crop disabled, uploading images directly');
        // Direct upload without crop (non-images already sent above)
        handleBatchUpload(imageFiles);
      }
    },
    [
      maxFiles,
      allowCrop,
      allowedTypes,
      onFilesSelected,
      handleBatchUpload,
      onUploadError,
      tUpload,
    ]
  );

  const handleCropSave = useCallback(
    async (croppedBlob: Blob) => {
      const currentItem = cropQueue[currentCropIndex];
      if (!currentItem) return;

      try {
        // Create a File from the Blob to preserve the filename
        const croppedFile = new File([croppedBlob], currentItem.file.name, {
          type: croppedBlob.type,
        });

        // Store the cropped file
        const newProcessedFiles = [...processedFiles, croppedFile];
        setProcessedFiles(newProcessedFiles);

        // Move to next image or complete
        if (currentCropIndex < cropQueue.length - 1) {
          setCurrentCropIndex(currentCropIndex + 1);
        } else {
          // All images processed - notify parent with cropped files
          onFilesProcessed?.(newProcessedFiles);
          // Don't call onUploadComplete here - let parent handle it
          cleanupCropQueue();
        }
      } catch (error) {
        console.error('Crop error:', error);
        onUploadError?.(error as Error);
        cleanupCropQueue();
      }
    },
    [
      cropQueue,
      currentCropIndex,
      processedFiles,
      onFilesProcessed,
      onUploadError,
      cleanupCropQueue,
    ]
  );

  const handleSkipCrop = useCallback(async () => {
    const currentItem = cropQueue[currentCropIndex];
    if (!currentItem) return;

    try {
      // Use original file without cropping
      const newProcessedFiles = [...processedFiles, currentItem.file];
      setProcessedFiles(newProcessedFiles);

      // Move to next image or complete
      if (currentCropIndex < cropQueue.length - 1) {
        setCurrentCropIndex(currentCropIndex + 1);
      } else {
        // All images processed - notify parent with original files
        onFilesProcessed?.(newProcessedFiles);
        // Don't call onUploadComplete here - let parent handle it
        cleanupCropQueue();
      }
    } catch (error) {
      console.error('Skip crop error:', error);
      onUploadError?.(error as Error);
      cleanupCropQueue();
    }
  }, [
    cropQueue,
    currentCropIndex,
    processedFiles,
    onFilesProcessed,
    onUploadError,
    cleanupCropQueue,
  ]);

  // ---- Instant path (opt-in via onInstantPreview) -------------------------

  // Advance to the next crop, or close the cropper immediately on the last one.
  const advanceOrCloseCrop = useCallback(() => {
    if (currentCropIndex < cropQueue.length - 1) {
      setCurrentCropIndex(currentCropIndex + 1);
    } else {
      cleanupCropQueue();
    }
  }, [currentCropIndex, cropQueue.length, cleanupCropQueue]);

  // Record a settled item; once every image in the batch has settled, hand the
  // cropped files to the parent. A null file = that image failed to compress.
  // `gen` is dropped if the batch was cancelled/replaced in the meantime.
  const settleInstantItem = useCallback(
    (file: File | null, gen: number) => {
      if (gen !== instantGenRef.current) return;
      if (file) instantAccumRef.current.push(file);
      instantPendingRef.current -= 1;
      if (instantPendingRef.current <= 0) {
        const files = instantAccumRef.current;
        instantAccumRef.current = [];
        if (files.length > 0) {
          onFilesProcessed?.(files);
        }
      }
    },
    [onFilesProcessed]
  );

  const handleCropConfirmed = useCallback(
    (previewUrl: string | null, blobPromise: Promise<Blob | null>) => {
      const currentItem = cropQueue[currentCropIndex];
      const gen = instantGenRef.current;
      // Optimistic placeholder card shows now; cropper closes now.
      if (previewUrl) onInstantPreview?.(previewUrl);
      advanceOrCloseCrop();
      // Compression already runs on a detached canvas — accumulate when done.
      blobPromise
        .then((blob) => {
          const file =
            blob && currentItem
              ? new File([blob], currentItem.file.name, { type: blob.type })
              : null;
          if (!file) {
            onUploadError?.(new Error('Failed to process cropped image'));
          }
          settleInstantItem(file, gen);
        })
        .catch((error) => {
          onUploadError?.(error as Error);
          settleInstantItem(null, gen);
        });
    },
    [
      cropQueue,
      currentCropIndex,
      onInstantPreview,
      advanceOrCloseCrop,
      settleInstantItem,
      onUploadError,
    ]
  );

  const handleSkipInstant = useCallback(() => {
    const currentItem = cropQueue[currentCropIndex];
    if (!currentItem) return;
    // Original file as-is. Fresh object URL for the card — the queue's own URL
    // is revoked on close; the consumer releases this one when the card clears.
    const previewUrl = URL.createObjectURL(currentItem.file);
    onInstantPreview?.(previewUrl);
    advanceOrCloseCrop();
    settleInstantItem(currentItem.file, instantGenRef.current);
  }, [
    cropQueue,
    currentCropIndex,
    onInstantPreview,
    advanceOrCloseCrop,
    settleInstantItem,
  ]);

  const handleCropCancel = useCallback(() => {
    // Invalidate the batch so any already-confirmed (background-compressing)
    // images don't fire a partial upload, and drop the optimistic card.
    instantGenRef.current += 1;
    onInstantPreview?.(null);
    cleanupCropQueue();
    // Don't report cancellation as an error - it's a normal user action
  }, [onInstantPreview, cleanupCropQueue]);

  // Expose openFileDialog method to parent components via ref
  useImperativeHandle(
    ref,
    () => ({
      openFileDialog: () => {
        if (showRendersOption) {
          // Show media source selector instead of direct file picker
          setShowMediaSourceSelector(true);
        } else {
          fileUploadRef.current?.openFileDialog();
        }
      },
      processFiles: (files: File[]) => {
        void handleFilesReceived(files);
      },
    }),
    [showRendersOption, handleFilesReceived]
  );

  // Handle files from media source selector: close the dialog and process
  // the files after its close animation finishes (avoids modal overlap with
  // the crop dialog). Scheduled directly — NOT via pending-state + effect:
  // an effect-owned timer is cancelled by its own cleanup as soon as any
  // dependency changes (clearing the pending state re-ran the effect), which
  // silently dropped the selected files before they were ever processed.
  const handleMediaSourceFiles = useCallback(
    (files: File[]) => {
      setShowMediaSourceSelector(false);
      setTimeout(() => {
        void handleFilesReceived(files);
      }, 150);
    },
    [handleFilesReceived]
  );

  const currentCropItem = cropQueue[currentCropIndex];

  // Log when crop modal should appear
  if (cropQueue.length > 0 && !currentCropItem) {
    console.error(
      '[UnifiedImageUpload] Crop queue exists but currentCropItem is undefined',
      {
        queueLength: cropQueue.length,
        currentCropIndex,
        cropQueue: cropQueue.map((item) => ({
          fileName: item.file.name,
          hasObjectUrl: !!item.objectUrl,
        })),
      }
    );
  }

  return (
    <>
      <FileUploadDropZone
        ref={fileUploadRef}
        onFilesSelected={handleFilesReceived}
        accept={accept}
        multiple={allowMultiple}
        maxFiles={maxFiles}
        className={className}
        transparent={transparent}
        title={uploadTitle || (children ? undefined : pickerTitle)}
        description={uploadDescription}
      >
        {children}
      </FileUploadDropZone>

      {/* Media source selector (when render picker is enabled) */}
      {showRendersOption && (
        <MediaSourceSelector
          isOpen={showMediaSourceSelector}
          onClose={() => setShowMediaSourceSelector(false)}
          onDismiss={onMediaSourceDismiss}
          onFilesSelected={handleMediaSourceFiles}
          config={{
            showRendersOption: true,
            allowMultiple,
            maxFiles,
            accept,
            title: pickerTitle,
            subtitle: pickerSubtitle,
          }}
          extraOptions={mediaSourceExtraOptions}
          projectAngles={mediaSourceProjectAngles}
          onProjectAngleSelected={onMediaSourceProjectAngleSelected}
        />
      )}

      {currentCropItem ? (
        <UnifiedImageCropModal
          imageUrl={currentCropItem.objectUrl}
          aspectRatio={cropAspectRatio}
          onSave={handleCropSave}
          onInstantConfirm={onInstantPreview ? handleCropConfirmed : undefined}
          onInstantSkip={
            onInstantPreview && cropQueue.length > 1
              ? handleSkipInstant
              : undefined
          }
          onCancel={handleCropCancel}
          onSkipCrop={cropQueue.length > 1 ? handleSkipCrop : undefined}
          minWidth={minCropWidth}
          minHeight={minCropHeight}
          showSkipButton={cropQueue.length > 1}
          freeAspectRatio={freeAspectRatio}
          sizePolicy={resolveImageSizePolicy(uploadType)}
          maxFileSize={uploadType === 'angle' ? 1500 * 1024 : 900 * 1024}
        />
      ) : null}
    </>
  );
});
